import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDialogA11y } from './useDialogA11y';
import { isTopDialog } from './dialog-stack';

/** Largeurs normalisées ; toutes se replient en pleine largeur sous `sm`. */
const sizeStyles = {
  sm: 'w-full sm:w-[480px]',
  md: 'w-full sm:w-[560px]',
  lg: 'w-full sm:w-[680px]',
} as const;

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Nom accessible quand il n'y a pas de `title` visible. */
  ariaLabel?: string;
  children: React.ReactNode;
  /** Pied : actions alignées à droite, comme dans une modale. */
  footer?: React.ReactNode;
  className?: string;
  /** `sm` consultation courte, `md` fiche, `lg` détail dense. */
  size?: keyof typeof sizeStyles;
}

export function Drawer({
  open,
  onClose,
  title,
  ariaLabel,
  children,
  footer,
  className,
  size = 'sm',
}: DrawerProps) {
  const { t } = useTranslation();
  const drawerRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const dialogId = useDialogA11y(open, drawerRef);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Seul le dialogue du dessus se ferme : une confirmation ouverte
      // par-dessus ne doit pas emporter son parent avec elle.
      if (e.key === 'Escape' && isTopDialog(dialogId)) onClose();
    }
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose, dialogId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-black/30 animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
        className={cn(
          'relative flex h-full flex-col bg-surface border-l border-border shadow-elevated animate-slide-in-right outline-none',
          sizeStyles[size],
          className,
        )}
      >
        {title && (
          <div data-dialog-header className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
            <h2 id={titleId} className="text-base font-semibold text-text">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              className="rounded p-1.5 text-subtle transition-colors hover:bg-surface-subtle hover:text-text"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-6 py-5 scrollbar-thin">
          {children}
        </div>
        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
